import {
  Component, ChangeDetectionStrategy, OnInit, OnDestroy, ChangeDetectorRef
} from '@angular/core';
import { Observable, Subscription, zip, of, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, tap, take } from 'rxjs/operators';
import isEmpty from 'lodash-es/isEmpty';

import { HistoryBookingDetail } from '@user-history/entities/history-booking-detail';
import { ReservationType } from '@user-history/entities/user-history-item';
import { AppFacade } from '@app/app.facade';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import { getReservationType } from '@user-history/mappers/user-history.mapper';
import { User } from '@app/modules/core/interfaces/user';
import { AbandonedReservationFacade } from '@abandoned-reservation/store/abandoned-reservation.facade';

@Component({
  selector: 'app-abandoned-reservation',
  templateUrl: './abandoned-reservation.container.html',
  styleUrls: ['./abandoned-reservation.container.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})

export class AbandonedReservationComponent implements OnInit, OnDestroy {

  public detailType: ReservationType;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private facade: AbandonedReservationFacade,
    private appFacade: AppFacade,
    private mobileDeviceService: MobileDeviceService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.subscriptions.add(
      this.isMobile$.pipe(
        distinctUntilChanged(),
        switchMap(isMobile => zip(
          of(isMobile),
          this.abandonedReservation$.pipe(filter(detail => !isEmpty(detail)), distinctUntilChanged())
        )),
        map(([isMobile, detail]) => ({ isMobile, detail })),
        tap(({ detail }) => {
          this.detailType = getReservationType(detail);
          this.cdr.detectChanges();
        })
      ).subscribe());
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.cdr.detach();
  }

  get isMobile$(): Observable<boolean> {
    return this.mobileDeviceService.isMobileDevice$;
  }

  get user$(): Observable<User> {
    return this.appFacade.user$;
  }

  get abandonedReservation$(): Observable<HistoryBookingDetail> {
    return this.facade.abandonedReservation$;
  }

  get isLoadingCancellation$(): Observable<boolean> {
    return this.facade.isLoadingCancellation$;
  }

  get hasAbandonedReservation$(): Observable<boolean> {
    return combineLatest([
      this.isMobile$, this.abandonedReservation$
    ]).pipe(
      map(([isMobile, abandonedReservation]) => {
        return !isEmpty(abandonedReservation) && isMobile;
      }));
  }

  get reservationType(): typeof ReservationType {
    return ReservationType;
  }

  public cancelAbandonedReservation(id: number): void {
    this.abandonedReservation$.pipe(
      take(1),
      tap(abandonedReservation => {
        this.facade.cancelAbandonedReservation(abandonedReservation.Id);
      })
    ).subscribe().unsubscribe();
  }

}
